import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Copy, Check, Send, RotateCcw, Save, FolderOpen, ChevronDown, ChevronUp, Search, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { contactService, aiService } from '../services/api';
import { useToast, ToastContainer } from '../componets/Toast';
import { useLocation } from 'react-router-dom';
import API_URL from '../config/API_URL';

// Template definitions for each communication medium
const MESSAGE_TEMPLATES = {
  email: [
    {
      value: '',
      label: 'No Template (AI decides structure)',
      description: 'Let AI determine the best structure'
    },
    {
      value: 'professional_standard',
      label: 'Professional Standard',
      description: 'Intro → Context → Ask → Reason → Outro',
      structure: 'Opening greeting → Brief personal connection or context → Clear request or question → Why this matters → Professional closing'
    },
    {
      value: 'warm_personal',
      label: 'Warm & Personal',
      description: 'Personal connection → Shared interest → Soft ask',
      structure: 'Warm opening → Personal observation or shared interest → Natural conversation → Gentle ask or invitation → Friendly closing'
    },
    {
      value: 'value_first',
      label: 'Value-First',
      description: 'Offer value → Brief intro → Optional ask',
      structure: 'Lead with something valuable (insight, resource, intro) → Brief context → Optional request → Closing'
    },
    {
      value: 'quick_check_in',
      label: 'Quick Check-In',
      description: 'Brief greeting → Specific question → Easy response',
      structure: 'Friendly opening → Specific, easy-to-answer question → Context if needed → Simple closing'
    }
  ],
  text: [
    {
      value: '',
      label: 'No Template (AI decides structure)',
      description: 'Let AI determine the best structure'
    },
    {
      value: 'casual_friendly',
      label: 'Casual & Friendly',
      description: 'Hey! → Personal note → Quick ask',
      structure: 'Casual greeting → Brief personal comment → Direct question or invitation → No formal sign-off needed'
    },
    {
      value: 'quick_question',
      label: 'Quick Question',
      description: 'Direct ask → Brief context',
      structure: 'Start with the question → One sentence of context if needed → Keep it conversational'
    },
    {
      value: 'thoughtful_reach',
      label: 'Thoughtful Reach-Out',
      description: 'Personal observation → Connection → Soft invitation',
      structure: 'Mention something specific about them → Make it personal → Casual invitation or question'
    }
  ],
  linkedin_message: [
    {
      value: '',
      label: 'No Template (AI decides structure)',
      description: 'Let AI determine the best structure'
    },
    {
      value: 'professional_connect',
      label: 'Professional Connection',
      description: 'Intro → Common ground → Specific ask',
      structure: 'Brief introduction → Shared connection or interest → Clear, specific request → Professional but friendly closing'
    },
    {
      value: 'mutual_benefit',
      label: 'Mutual Benefit',
      description: 'Context → Value proposition → Invitation',
      structure: 'Why you\'re reaching out → What you can offer → Specific invitation to connect or collaborate'
    },
    {
      value: 'congratulatory',
      label: 'Congratulatory Follow-Up',
      description: 'Congratulations → Personal note → Natural next step',
      structure: 'Reference their achievement → Personal observation → Natural invitation to connect further'
    },
    {
      value: 'content_engagement',
      label: 'Content Engagement',
      description: 'React to their content → Add value → Connect',
      structure: 'Reference their post/article → Add your perspective or insight → Invitation to continue conversation'
    }
  ]
};

const GenerateMessage = () => {
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState('');
  const [messageType, setMessageType] = useState('follow_up');
  const [context, setContext] = useState('');
  const [medium, setMedium] = useState('email');
  const [template, setTemplate] = useState('');

  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Conversation state
  const [conversationHistory, setConversationHistory] = useState([]);
  const [refinementInput, setRefinementInput] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  
  // Saved messages state
  const [showSavedMessages, setShowSavedMessages] = useState(false);
  const [savedMessages, setSavedMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  
  const messagesEndRef = useRef(null);

  const [showTemplates, setShowTemplates] = useState(false);

  // Toast notifications
  const { toasts, removeToast, showSuccess, showError, showWarning, showInfo } = useToast();

  const styles = {
    page: {
      padding: '2rem',
      maxWidth: '1000px',
      margin: '0 auto',
    },
    header: {
      marginBottom: '2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerLeft: {
      flex: 1,
    },
    title: {
      fontSize: '2rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    subtitle: {
      color: '#475569',
      fontSize: '1rem',
    },
    savedMessagesButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.25rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#ffffff',
      color: '#475569',
    },
    card: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '2rem',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '1.5rem',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#0f172a',
      fontWeight: 500,
      fontSize: '0.95rem',
    },
    select: {
      width: '100%',
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      cursor: 'pointer',
    },
    textarea: {
      width: '96%',
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
      width: '100%',
    },
    templateCard: {
      padding: '1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 200ms',
      marginBottom: '0.75rem',
      background: '#ffffff',
    },
    templateCardSelected: {
      borderColor: '#667eea',
      background: '#eff6ff',
      boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
    },
    templateLabel: {
      fontWeight: 600,
      fontSize: '0.95rem',
      color: '#0f172a',
      marginBottom: '0.25rem',
      display: 'block',
    },
    templateDescription: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginBottom: '0.5rem',
    },
    templateStructure: {
      fontSize: '0.8rem',
      color: '#475569',
      fontStyle: 'italic',
      lineHeight: 1.5,
      paddingTop: '0.5rem',
      borderTop: '1px solid #e2e8f0',
    },
    conversationContainer: {
      maxHeight: '500px',
      overflowY: 'auto',
      marginBottom: '1rem',
      padding: '0.5rem',
    },
    messageItem: {
      marginBottom: '1rem',
      padding: '1rem',
      borderRadius: '12px',
      position: 'relative',
    },
    userMessage: {
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      marginLeft: '2rem',
    },
    aiMessage: {
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      marginRight: '2rem',
    },
    messageLabel: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#64748b',
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    messageText: {
      fontSize: '0.95rem',
      lineHeight: 1.6,
      color: '#0f172a',
      whiteSpace: 'pre-wrap',
    },
    actionButtonsContainer: {
      position: 'absolute',
      top: '0.75rem',
      right: '0.75rem',
      display: 'flex',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.4rem 0.75rem',
      border: 'none',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
    },
    copyButton: {
      background: '#2563eb',
      color: 'white',
    },
    saveButton: {
      background: '#8b5cf6',
      color: 'white',
    },
    refinementInput: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-end',
    },
    refinementTextarea: {
      flex: 1,
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.95rem',
      transition: 'all 200ms',
      fontFamily: 'inherit',
      background: '#ffffff',
      outline: 'none',
      minHeight: '80px',
      resize: 'vertical',
    },
    sendButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.25rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
      whiteSpace: 'nowrap',
      height: 'fit-content',
    },
    resetButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.25rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#ffffff',
      color: '#64748b',
      marginTop: '1rem',
    },
    hint: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginTop: '0.5rem',
    },
    contactInfo: {
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '8px',
      padding: '1rem',
      marginTop: '1rem',
    },
    contactInfoTitle: {
      fontWeight: 600,
      fontSize: '0.95rem',
      color: '#1e40af',
      marginBottom: '0.5rem',
    },
    contactInfoText: {
      fontSize: '0.85rem',
      color: '#475569',
      lineHeight: 1.5,
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '3rem 1rem',
    },
    loadingDots: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
    },
    loadingDot: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      background: '#667eea',
    },
    savedMessagesList: {
      display: 'grid',
      gap: '1rem',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    },
    savedMessageCard: {
      padding: '1rem',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      background: '#ffffff',
      cursor: 'pointer',
      transition: 'all 200ms',
    },
    savedMessageTitle: {
      fontWeight: 600,
      fontSize: '0.95rem',
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    savedMessagePreview: {
      fontSize: '0.85rem',
      color: '#64748b',
      lineHeight: 1.5,
      marginBottom: '0.75rem',
    },
    savedMessageMeta: {
      fontSize: '0.75rem',
      color: '#94a3b8',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    continueNotice: {
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '8px',
      padding: '1rem',
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: '#1e40af',
      fontSize: '0.9rem',
    },
    expandableHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 200ms',
      background: '#ffffff',
      marginBottom: '0.75rem',
    },
    expandableHeaderOpen: {
      borderBottomLeftRadius: '0',
      borderBottomRightRadius: '0',
      marginBottom: '0',
      borderColor: '#cbd5e1',
      background: '#f8fafc',
    },
    expandableHeaderText: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    },
    expandableTitle: {
      fontWeight: 600,
      fontSize: '0.95rem',
      color: '#0f172a',
    },
    expandableSubtitle: {
      fontSize: '0.85rem',
      color: '#64748b',
    },
    expandableContent: {
      border: '2px solid #cbd5e1',
      borderTop: 'none',
      borderBottomLeftRadius: '12px',
      borderBottomRightRadius: '12px',
      padding: '1rem',
      background: '#f8fafc',
      marginBottom: '0.75rem',
    },
    selectedTemplateBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.25rem 0.75rem',
      background: '#667eea',
      color: 'white',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
    },
    searchContainer: {
      marginBottom: '1.5rem',
      position: 'relative',
    },
    searchInputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    searchIcon: {
      position: 'absolute',
      left: '1rem',
      color: '#64748b',
      pointerEvents: 'none',
    },
    searchInput: {
      width: '100%',
      padding: '0.875rem 1rem 0.875rem 3rem',
      borderRadius: '12px',
      border: '2px solid #e2e8f0',
      fontSize: '0.95rem',
      transition: 'all 0.2s',
      outline: 'none',
      background: '#f8fafc',
    },
    searchingIndicator: {
      position: 'absolute',
      right: '1rem',
      color: '#667eea',
      fontSize: '0.85rem',
      fontWeight: 500,
    },
    clearButton: {
      position: 'absolute',
      right: '1rem',
      background: '#f1f5f9',
      border: 'none',
      borderRadius: '6px',
      padding: '0.5rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      color: '#64748b',
      transition: 'all 0.2s',
    },
    emptyState: {
      textAlign: 'center',
      padding: '2rem',
      color: '#64748b',
    },
  };

  useEffect(() => {
    loadContacts();
    loadSavedMessages();
  }, []);

  // Reset template when medium changes
  useEffect(() => {
    setTemplate('');
  }, [medium]);

  // Load data from Dashboard navigation
  useEffect(() => {
    if (location.state) {
      const { selectedContact: contact, messageType: type, medium: med, generatedMessage } = location.state;
      
      if (contact) setSelectedContact(contact);
      if (type) setMessageType(type);
      if (med) setMedium(med);
      
      // If there's a generated message, add it to conversation history
      if (generatedMessage) {
        setConversationHistory([
          { role: 'assistant', content: generatedMessage }
        ]);
        showInfo('Message loaded from Quick Generator! Continue refining below.');
      }
    }
  }, [location.state]);

  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory, isRefining]);

  useEffect(() => {
    setTemplate('');
    setShowTemplates(false); // Auto-collapse when medium changes
  }, [medium]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadContacts = async () => {
    try {
      const data = await contactService.getContacts();
      setContacts(data.contacts);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      showError('Failed to load contacts');
    }
  };

  const loadSavedMessages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/saved-messages`, {
        credentials: 'include'
      });
      const data = await response.json();
      setSavedMessages(data.saved_messages);
    } catch (error) {
      console.error('Failed to load saved messages:', error);
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults(null);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `${API_URL}/api/saved-messages/search?q=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      setSearchResults(data.saved_messages || []);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const selectedContactData = contacts.find(c => c.id === parseInt(selectedContact));
  const currentTemplates = MESSAGE_TEMPLATES[medium] || MESSAGE_TEMPLATES.email;

  const handleGenerate = async () => {
    if (!selectedContact) {
      showWarning('Please select a contact first');
      return;
    }

    setLoading(true);
    setConversationHistory([]);
    setSaved(false);

    try {
      const result = await aiService.generateMessage(
        parseInt(selectedContact),
        messageType,
        medium,
        context,
        template
      );
      
      setConversationHistory([
        { role: 'assistant', content: result.message }
      ]);
      showSuccess('Message generated successfully!');
    } catch (error) {
      console.error('Failed to generate message:', error);
      showError('Failed to generate message. Please try again.');
      setConversationHistory([
        { role: 'assistant', content: 'Failed to generate message. Please try again.' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefine = async () => {
    if (!refinementInput.trim()) return;

    const userMessage = refinementInput.trim();
    setRefinementInput('');
    setIsRefining(true);
    setSaved(false);

    const updatedHistory = [
      ...conversationHistory,
      { role: 'user', content: userMessage }
    ];
    setConversationHistory(updatedHistory);

    try {
      const result = await aiService.refineMessage(
        parseInt(selectedContact),
        messageType,
        medium,
        context,
        updatedHistory,
        template
      );
      
      setConversationHistory([
        ...updatedHistory,
        { role: 'assistant', content: result.message }
      ]);
      showSuccess('Message refined successfully!');
    } catch (error) {
      console.error('Failed to refine message:', error);
      showError('Failed to refine message. Please try again.');
      setConversationHistory([
        ...updatedHistory,
        { role: 'assistant', content: 'Sorry, I had trouble refining the message. Please try again.' }
      ]);
    } finally {
      setIsRefining(false);
    }
  };

  const handleSaveMessage = async (messageContent, index) => {
    setSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/save-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          contact_id: parseInt(selectedContact),
          message_content: messageContent,
          message_type: messageType,
          medium: medium
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSaved(index);
        setTimeout(() => setSaved(false), 2000);
        loadSavedMessages();
        showSuccess('Message saved successfully! 🎉');
      } else {
        showError(data.error || 'Failed to save message');
      }
    } catch (error) {
      console.error('Failed to save message:', error);
      showError('Failed to save message. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadSavedMessage = async (messageId) => {
    try {
      const response = await fetch(`${API_URL}/api/saved-messages/${messageId}`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      if (response.ok) {
        setSelectedContact(data.contact.id.toString());
        setMessageType(data.message_type);
        setMedium(data.medium);
        setConversationHistory([
          { role: 'assistant', content: data.content }
        ]);
        setShowSavedMessages(false);
        showInfo('Saved message loaded!');
      } else {
        showError('Failed to load message');
      }
    } catch (error) {
      console.error('Failed to load saved message:', error);
      showError('Failed to load message. Please try again.');
    }
  };

  const handleReset = () => {
    setConversationHistory([]);
    setRefinementInput('');
    setCopied(false);
    setSaved(false);
    showInfo('Conversation reset');
  };

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopied(index);
    setTimeout(() => setCopied(false), 2000);
    showSuccess('Copied to clipboard!');
  };

  const LoadingDots = () => (
    <div style={styles.loadingContainer}>
      <div style={styles.loadingDots}>
        <motion.div
          style={styles.loadingDot}
          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0 }}
        />
        <motion.div
          style={styles.loadingDot}
          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
        />
        <motion.div
          style={styles.loadingDot}
          animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
        />
      </div>
    </div>
  );

  // Determine which messages to display
  const displayedMessages = searchQuery.trim() 
    ? (searchResults !== null ? searchResults : [])
    : savedMessages;

  const showEmptyState = displayedMessages.length === 0;
  const isSearchEmpty = searchQuery.trim() && displayedMessages.length === 0;

  return (
    <div style={styles.page}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.title}>Generate Message</h1>
          <p style={styles.subtitle}>
            AI-powered networking messages tailored to your contacts
          </p>
        </div>
        <button
          onClick={() => setShowSavedMessages(!showSavedMessages)}
          style={styles.savedMessagesButton}
          onMouseEnter={(e) => {
            e.target.style.borderColor = '#cbd5e1';
            e.target.style.background = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.background = '#ffffff';
          }}
        >
          <FolderOpen size={18} />
          Saved Messages ({savedMessages.length})
        </button>
      </div>

      {/* Continue from Dashboard Notice */}
      {location.state?.generatedMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.continueNotice}
        >
          <Sparkles size={18} />
          <span>Continuing from Quick Generator - refine your message below!</span>
        </motion.div>
      )}

      {/* Saved Messages Panel */}
      {showSavedMessages && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.card}
        >
          <h3 style={{ ...styles.label, fontSize: '1.1rem', marginBottom: '1rem' }}>
            Your Saved Messages
          </h3>
          
          {/* Search Bar */}
          <div style={styles.searchContainer}>
            <div style={styles.searchInputWrapper}>
              <Search size={18} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search saved messages..."
                value={searchQuery}
                onChange={(e) => {
                  const query = e.target.value;
                  setSearchQuery(query);
                  if (query.trim().length >= 2) {
                    handleSearch(query);
                  } else {
                    setSearchResults(null);
                  }
                }}
                style={styles.searchInput}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {isSearching && (
                <span style={styles.searchingIndicator}>Searching...</span>
              )}
              {searchQuery && !isSearching && (
                <button
                  style={styles.clearButton}
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults(null);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e2e8f0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                  }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
          
          {showEmptyState ? (
            <div style={styles.emptyState}>
              <FolderOpen size={48} style={{ color: '#cbd5e1', marginBottom: '0.5rem' }} />
              <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                {isSearchEmpty ? 'No messages found' : 'No saved messages yet'}
              </p>
              <p style={{ fontSize: '0.85rem' }}>
                {isSearchEmpty 
                  ? `No messages match "${searchQuery}"`
                  : 'Generate and save messages to see them here'
                }
              </p>
            </div>
          ) : (
            <div style={styles.savedMessagesList}>
              {displayedMessages.map((msg) => (
                <div
                  key={msg.id}
                  style={styles.savedMessageCard}
                  onClick={() => handleLoadSavedMessage(msg.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#2563eb';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={styles.savedMessageTitle}>{msg.title}</div>
                  <div style={styles.savedMessagePreview}>{msg.preview}</div>
                  <div style={styles.savedMessageMeta}>
                    <span>{msg.contact.name} • {msg.medium}</span>
                    <span>{new Date(msg.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Generation Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={styles.card}
      >
        <div style={styles.formGroup}>
          <label style={styles.label}>Select Contact *</label>
          <select
            value={selectedContact}
            onChange={(e) => setSelectedContact(e.target.value)}
            style={styles.select}
            onFocus={(e) => {
              e.target.style.borderColor = '#2563eb';
              e.target.style.background = '#ffffff';
              e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.background = '#f8fafc';
              e.target.style.boxShadow = 'none';
            }}
          >
            <option value="">Choose a contact...</option>
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.name} {contact.company ? `- ${contact.company}` : ''}
              </option>
            ))}
          </select>

          {selectedContactData && (
            <div style={styles.contactInfo}>
              <div style={styles.contactInfoTitle}>Contact Context:</div>
              <div style={styles.contactInfoText}>
                <strong>Connection:</strong> {selectedContactData.connection_origin || 'N/A'}<br />
                <strong>Interests:</strong> {selectedContactData.interests || 'N/A'}<br />
                {selectedContactData.conversation_notes && (
                  <>
                    <strong>Notes:</strong> {selectedContactData.conversation_notes}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Message Type</label>
          <select
            value={messageType}
            onChange={(e) => setMessageType(e.target.value)}
            style={styles.select}
            onFocus={(e) => {
              e.target.style.borderColor = '#2563eb';
              e.target.style.background = '#ffffff';
              e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.background = '#f8fafc';
              e.target.style.boxShadow = 'none';
            }}
          >
            <option value="follow_up">Follow-up</option>
            <option value="introduction">Introduction</option>
            <option value="response">Response</option>
            <option value="check_in">Check-in</option>
            <option value="thank_you">Thank You</option>
          </select>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Communication Medium</label>
          <select
            value={medium}
            onChange={(e) => setMedium(e.target.value)}
            style={styles.select}
            onFocus={(e) => {
              e.target.style.borderColor = '#2563eb';
              e.target.style.background = '#ffffff';
              e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.background = '#f8fafc';
              e.target.style.boxShadow = 'none';
            }}
          >
            <option value="email">Email</option>
            <option value="text">Text Message</option>
            <option value="linkedin_message">LinkedIn Message</option>
          </select>
          <p style={styles.hint}>
            {medium === 'email' && 'Professional and structured with subject line'}
            {medium === 'text' && 'Casual and brief, like texting a friend'}
            {medium === 'linkedin_message' && 'Professional but conversational'}
          </p>
        </div>

        {/* Template Selection */}
        <div style={styles.formGroup}>
          <div
            onClick={() => setShowTemplates(!showTemplates)}
            style={{
              ...styles.expandableHeader,
              ...(showTemplates ? styles.expandableHeaderOpen : {})
            }}
            onMouseEnter={(e) => {
              if (!showTemplates) {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.background = '#f8fafc';
              }
            }}
            onMouseLeave={(e) => {
              if (!showTemplates) {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.background = '#ffffff';
              }
            }}
          >
            <div style={styles.expandableHeaderText}>
              <div style={styles.expandableTitle}>
                Message Structure {template && `• ${currentTemplates.find(t => t.value === template)?.label}`}
              </div>
              <div style={styles.expandableSubtitle}>
                {template 
                  ? 'Template selected - Click to change or remove'
                  : 'Optional: Choose a template to guide AI generation'
                }
              </div>
            </div>
            {showTemplates ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>

          <AnimatePresence>
            {showTemplates && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ overflow: 'hidden' }}
              >
                <div style={styles.expandableContent}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    {currentTemplates.map((tmpl) => (
                      <div
                        key={tmpl.value}
                        onClick={() => {
                          setTemplate(tmpl.value);
                        }}
                        style={{
                          ...styles.templateCard,
                          ...(template === tmpl.value ? styles.templateCardSelected : {})
                        }}
                        onMouseEnter={(e) => {
                          if (template !== tmpl.value) {
                            e.currentTarget.style.borderColor = '#cbd5e1';
                            e.currentTarget.style.background = '#f8fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (template !== tmpl.value) {
                            e.currentTarget.style.borderColor = '#e2e8f0';
                            e.currentTarget.style.background = '#ffffff';
                          }
                        }}
                      >
                        <span style={styles.templateLabel}>{tmpl.label}</span>
                        <div style={styles.templateDescription}>{tmpl.description}</div>
                        {tmpl.structure && template === tmpl.value && (
                          <div style={styles.templateStructure}>
                            {tmpl.structure}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>


        <div style={styles.formGroup}>
          <label style={styles.label}>Additional Context (Optional)</label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g., Bentley lacrosse has a game this Saturday, or I saw their recent LinkedIn post about..."
            style={styles.textarea}
            onFocus={(e) => {
              e.target.style.borderColor = '#2563eb';
              e.target.style.background = '#ffffff';
              e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.background = '#f8fafc';
              e.target.style.boxShadow = 'none';
            }}
          />
          <p style={styles.hint}>
            Add any timely events, recent news, or specific topics to make the message more relevant
          </p>
        </div>

        <button
          onClick={handleGenerate}
          disabled={loading || !selectedContact}
          style={{
            ...styles.button,
            opacity: loading || !selectedContact ? 0.5 : 1,
            cursor: loading || !selectedContact ? 'not-allowed' : 'pointer',
          }}
          onMouseEnter={(e) => {
            if (!loading && selectedContact) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading && selectedContact) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
            }
          }}
        >
          <Sparkles size={20} />
          {loading ? 'Generating...' : 'Generate Message'}
        </button>
      </motion.div>

      {/* Loading state */}
      {loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.card}
        >
          <LoadingDots />
        </motion.div>
      )}

      {/* Conversation */}
      {conversationHistory.length > 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.card}
        >
          <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={styles.label}>Conversation</label>
            <button
              onClick={handleReset}
              style={{
                ...styles.resetButton,
                marginTop: 0,
                padding: '0.5rem 1rem',
                fontSize: '0.85rem',
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#cbd5e1';
                e.target.style.background = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.background = '#ffffff';
              }}
            >
              <RotateCcw size={14} />
              Start Over
            </button>
          </div>
          
          <div style={styles.conversationContainer}>
            <AnimatePresence>
              {conversationHistory.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    ...styles.messageItem,
                    ...(msg.role === 'user' ? styles.userMessage : styles.aiMessage)
                  }}
                >
                  <div style={styles.messageLabel}>
                    {msg.role === 'user' ? 'You' : 'AI Generated Message'}
                  </div>
                  <div style={styles.messageText}>{msg.content}</div>
                  {msg.role === 'assistant' && (
                    <div style={styles.actionButtonsContainer}>
                      <button
                        onClick={() => handleCopy(msg.content, idx)}
                        style={{
                          ...styles.actionButton,
                          ...styles.copyButton,
                          ...(copied === idx ? styles.copyButtonCopied : {})
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        {copied === idx ? <Check size={14} /> : <Copy size={14} />}
                        {copied === idx ? 'Copied!' : 'Copy'}
                      </button>
                      <button
                        onClick={() => handleSaveMessage(msg.content, idx)}
                        disabled={saving}
                        style={{
                          ...styles.actionButton,
                          ...styles.saveButton,
                          ...(saved === idx ? styles.saveButtonSaved : {}),
                          opacity: saving ? 0.6 : 1,
                          cursor: saving ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          if (!saving) {
                            e.target.style.transform = 'translateY(-2px)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!saving) {
                            e.target.style.transform = 'translateY(0)';
                          }
                        }}
                      >
                        {saved === idx ? <Check size={14} /> : <Save size={14} />}
                        {saved === idx ? 'Saved!' : saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
            <div ref={messagesEndRef} />
          </div>

          {/* Refinement section */}
          <div style={{ marginTop: '1.5rem' }}>
            <label style={styles.label}>Refine Message</label>
            <p style={styles.hint}>
              Ask for changes like "make it shorter", "add more enthusiasm", or "mention their recent promotion"
            </p>
            <div style={styles.refinementInput}>
              <textarea
                value={refinementInput}
                onChange={(e) => setRefinementInput(e.target.value)}
                placeholder="e.g., Make it more casual and add a reference to coffee..."
                style={styles.refinementTextarea}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.metaKey) {
                    handleRefine();
                  }
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <button
                onClick={handleRefine}
                disabled={isRefining || !refinementInput.trim()}
                style={{
                  ...styles.sendButton,
                  opacity: isRefining || !refinementInput.trim() ? 0.5 : 1,
                  cursor: isRefining || !refinementInput.trim() ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!isRefining && refinementInput.trim()) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isRefining && refinementInput.trim()) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }
                }}
              >
                <Send size={18} />
                {isRefining ? 'Refining...' : 'Send'}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default GenerateMessage;